/** Per-employee navbar punch worklog (local fallback when API is slow/offline).
 * Supports multi-session punches with First IN / Last OUT aggregation.
 *
 * Access ESS (proxy) sessions use sessionStorage so admin punches in localStorage
 * never leak into another employee's ESS window (and vice versa).
 */

import { isEssProxySession } from "../utils/authStorage";
import { assignPunchToAttendanceDay } from "./attendancePunchDayAssigner";
import {
  loadAttendancePolicies,
  resolvePolicyForEmployee,
  resolveShiftForEmployeeDay,
} from "./attendancePolicyEngine";

export const ATTENDANCE_PUNCH_UPDATED_EVENT = "hrms-attendance-punch-updated";

export const LEGACY_CHECKIN_KEY = "adminHomeCheckInAt";
export const LEGACY_WORKLOG_KEY = "adminHomeWorkLogByDay";
export const LEGACY_LAST_SESSION_KEY = "adminHomeLastSession";

export const employeeCheckInKey = (employeeId) =>
  employeeId != null ? `hrms_employee_checkin_${String(employeeId)}` : null;

export const employeeWorklogKey = (employeeId) =>
  employeeId != null ? `hrms_employee_worklog_${String(employeeId)}` : null;

export const employeeLastSessionKey = (employeeId) =>
  employeeId != null ? `hrms_employee_last_session_${String(employeeId)}` : null;

export const employeeCheckoutGuardKey = (employeeId, dayKey) =>
  employeeId != null && dayKey
    ? `hrms_checkout_guard_${String(employeeId)}_${dayKey}`
    : null;

export function markLocalCheckoutGuard(employeeId, dayKey = getLocalDayKey()) {
  const store = punchStore();
  const key = employeeCheckoutGuardKey(employeeId, dayKey);
  if (!store || !key) return;
  try {
    store.setItem(key, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function clearLocalCheckoutGuard(employeeId, dayKey = getLocalDayKey()) {
  const store = punchStore();
  const key = employeeCheckoutGuardKey(employeeId, dayKey);
  if (!store || !key) return;
  try {
    store.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function hasLocalCheckoutGuard(employeeId, dayKey = getLocalDayKey()) {
  const store = punchStore();
  const key = employeeCheckoutGuardKey(employeeId, dayKey);
  if (!store || !key) return false;
  try {
    return Boolean(store.getItem(key));
  } catch {
    return false;
  }
}

function isClosedWorklogRow(row) {
  return Boolean(row?.inMs && row.outMs != null && !row.open);
}

function punchStore() {
  if (typeof window === "undefined") return null;
  try {
    return isEssProxySession() ? window.sessionStorage : window.localStorage;
  } catch {
    return window.localStorage;
  }
}

function allowLegacyMirror() {
  return typeof window !== "undefined" && !isEssProxySession();
}

export function getLocalDayKey(ms = Date.now()) {
  const d = new Date(ms);

  if (Number.isNaN(d.getTime())) {
    return null;
  }

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);

  const values = {};

  for (const part of parts) {
    if (part.type !== "literal") {
      values[part.type] = part.value;
    }
  }

  return `${values.year}-${values.month}-${values.day}`;
}

/** Punch clock duration (matches navbar Work Hours). */
export function formatDurationHms(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function formatElapsedHms(startMs, endMs) {
  if (startMs == null || endMs == null || !Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return "—";
  }
  return formatDurationHms(Math.max(0, endMs - startMs));
}

/**
 * At most one open session: consecutive INs collapse to earliest IN (A1).
 * Closed pairs are unchanged.
 */
export function collapseOpenSessions(sessions = []) {
  const list = (Array.isArray(sessions) ? sessions : [])
    .map(normalizeSession)
    .filter(Boolean)
    .sort((a, b) => a.inMs - b.inMs);
  if (list.length === 0) return [];
  const closed = list.filter((s) => s.outMs != null);
  const opens = list.filter((s) => s.outMs == null);
  if (opens.length === 0) return closed;
  const earliestOpen = opens.reduce((a, b) => (a.inMs <= b.inMs ? a : b));
  return [...closed, { inMs: earliestOpen.inMs, outMs: null }].sort(
    (a, b) => a.inMs - b.inMs
  );
}

/** Sum of punch sessions. At most one live open segment uses nowMs. */
export function workedMsFromSessions(row, nowMs = Date.now()) {
  if (!row) return 0;
  const sessions = collapseOpenSessions(
    Array.isArray(row.sessions) && row.sessions.length
      ? row.sessions
      : row.inMs
        ? [{ inMs: row.inMs, outMs: row.outMs ?? null }]
        : []
  );
  const allowLive = row.open === true;
  let total = 0;
  let liveCounted = false;
  for (const s of sessions) {
    const inMs = Number(s?.inMs);
    if (!Number.isFinite(inMs) || inMs <= 0) continue;
    if (s?.outMs == null) {
      if (!allowLive || liveCounted) continue;
      liveCounted = true;
      total += Math.max(0, nowMs - inMs);
      continue;
    }
    const outMs = Number(s.outMs);
    if (!Number.isFinite(outMs)) continue;
    total += Math.max(0, outMs - inMs);
  }
  return total;
}

function toFiniteMs(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Normalize a single session { inMs, outMs|null }. */
export function normalizeSession(session) {
  if (!session || typeof session !== "object") return null;
  const inMs = toFiniteMs(session.inMs);
  if (inMs == null) return null;
  let outMs = session.outMs == null ? null : toFiniteMs(session.outMs);
  if (session.outMs != null && outMs == null) return null;
  if (outMs != null && outMs < inMs) outMs = inMs;
  return { inMs, outMs };
}

/**
 * Aggregate sessions → First IN / Last OUT.
 * Collapses multiple open sessions to a single earliest-IN open (A1).
 * @returns {{ inMs: number, outMs: number|null, sessions: Array, open: boolean }|null}
 */
export function aggregateFirstInLastOut(sessions = []) {
  const list = collapseOpenSessions(sessions);
  if (list.length === 0) return null;
  const firstIn = list[0].inMs;
  const open = list.some((s) => s.outMs == null);
  let lastOut = null;
  for (const s of list) {
    if (s.outMs != null && (lastOut == null || s.outMs > lastOut)) {
      lastOut = s.outMs;
    }
  }
  return {
    inMs: firstIn,
    outMs: open ? null : lastOut,
    sessions: list,
    open,
  };
}

/**
 * Normalize stored day row (legacy single pair or multi-session).
 * Always returns FILO shape with sessions[] when valid.
 */
export function normalizeWorklogRow(row) {
  if (!row || typeof row !== "object") return null;

  if (Array.isArray(row.sessions) && row.sessions.length > 0) {
    return aggregateFirstInLastOut(row.sessions);
  }

  const inMs = toFiniteMs(row.inMs);
  if (inMs == null) return null;
  const outMs = row.outMs == null ? null : toFiniteMs(row.outMs);
  if (row.outMs != null && outMs == null) return null;
  return aggregateFirstInLastOut([{ inMs, outMs }]);
}

export function readEmployeeWorklog(employeeId, dayKey = getLocalDayKey()) {
  if (!employeeId) return null;
  const store = punchStore();
  if (!store) return null;
  try {
    const raw = store.getItem(employeeWorklogKey(employeeId));
    if (!raw) {
      if (allowLegacyMirror() && String(employeeId) === readLegacyEmployeeId()) {
        return readLegacyWorklogRow(dayKey);
      }
      return null;
    }
    const parsed = JSON.parse(raw);
    const rawRow = parsed?.[dayKey];
    const normalized = normalizeWorklogRow(rawRow);
    // Self-heal dirty multi-open rows left by older pairing.
    if (
      normalized &&
      Array.isArray(rawRow?.sessions) &&
      rawRow.sessions.filter((s) => s && s.outMs == null).length > 1
    ) {
      writeWorklogDay(employeeId, dayKey, normalized);
    }
    return normalized;
  } catch {
    return null;
  }
}

export function readEmployeeWorklogMap(employeeId) {
  if (!employeeId) return {};
  const store = punchStore();
  if (!store) return {};
  try {
    const raw = store.getItem(employeeWorklogKey(employeeId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const out = {};
    Object.keys(parsed).forEach((k) => {
      const n = normalizeWorklogRow(parsed[k]);
      if (n) out[k] = n;
    });
    return out;
  } catch {
    return {};
  }
}

function readLegacyEmployeeId() {
  try {
    return localStorage.getItem("hrms_current_employee_id") || "";
  } catch {
    return "";
  }
}

function readLegacyWorklogRow(dayKey) {
  try {
    const raw = localStorage.getItem(LEGACY_WORKLOG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return normalizeWorklogRow(parsed?.[dayKey]);
  } catch {
    return null;
  }
}

function writeWorklogDay(employeeId, dayKey, aggregated) {
  if (!employeeId || !aggregated?.inMs) return;
  const store = punchStore();
  if (!store) return;
  const payload = {
    inMs: aggregated.inMs,
    outMs: aggregated.outMs ?? null,
    sessions: aggregated.sessions || [{ inMs: aggregated.inMs, outMs: aggregated.outMs ?? null }],
  };
  try {
    const key = employeeWorklogKey(employeeId);
    const raw = store.getItem(key);
    const parsed = raw ? JSON.parse(raw) : {};
    const next = parsed && typeof parsed === "object" ? parsed : {};
    next[dayKey] = payload;
    store.setItem(key, JSON.stringify(next));
    if (allowLegacyMirror()) {
      localStorage.setItem("hrms_current_employee_id", String(employeeId));
      if (
        readLegacyEmployeeId() === String(employeeId) ||
        !localStorage.getItem(LEGACY_WORKLOG_KEY)
      ) {
        const legacyRaw = localStorage.getItem(LEGACY_WORKLOG_KEY);
        const legacy = legacyRaw ? JSON.parse(legacyRaw) : {};
        const legacyNext = legacy && typeof legacy === "object" ? legacy : {};
        legacyNext[dayKey] = payload;
        localStorage.setItem(LEGACY_WORKLOG_KEY, JSON.stringify(legacyNext));
      }
    }
  } catch {
    /* ignore */
  }
  dispatchPunchUpdated(employeeId, dayKey);
}

/** Persist FILO day row (compat for callers that pass a single pair). */
export function saveEmployeeWorklogDay(employeeId, inMs, outMs, sessions = null) {
  if (!employeeId || !Number.isFinite(inMs) || inMs <= 0) return;
  const dayKey = getLocalDayKey(inMs);
  const aggregated =
    sessions != null
      ? aggregateFirstInLastOut(sessions)
      : aggregateFirstInLastOut([{ inMs, outMs: outMs ?? null }]);
  if (!aggregated) return;
  writeWorklogDay(employeeId, dayKey, aggregated);
}

export function dispatchPunchUpdated(employeeId, dayKey) {
  try {
    window.dispatchEvent(
      new CustomEvent(ATTENDANCE_PUNCH_UPDATED_EVENT, {
        detail: { employeeId: String(employeeId), dayKey },
      })
    );
  } catch {
    /* ignore */
  }
}

export function readStoredCheckIn(employeeId) {
  const store = punchStore();
  if (!store) return { checkedIn: false, at: null };
  // Never fall back to shared legacy keys in Access ESS — that leaks the admin punch.
  if (!employeeId && !allowLegacyMirror()) {
    return { checkedIn: false, at: null };
  }
  const key = employeeId ? employeeCheckInKey(employeeId) : LEGACY_CHECKIN_KEY;
  try {
    const raw = store.getItem(key);
    if (!raw && employeeId && allowLegacyMirror()) {
      // Only the active admin employee may read legacy single-key check-in.
      if (String(employeeId) === readLegacyEmployeeId()) {
        const legacy = localStorage.getItem(LEGACY_CHECKIN_KEY);
        if (legacy) {
          const t = Number(legacy);
          if (Number.isFinite(t) && t > 0) return { checkedIn: true, at: t };
        }
      }
      return { checkedIn: false, at: null };
    }
    if (!raw) return { checkedIn: false, at: null };
    const t = Number(raw);
    if (Number.isFinite(t) && t > 0) return { checkedIn: true, at: t };
  } catch {
    /* ignore */
  }
  return { checkedIn: false, at: null };
}

export function readLastSessionStored(employeeId) {
  const store = punchStore();
  if (!store) return null;
  if (!employeeId && !allowLegacyMirror()) return null;
  const key = employeeId ? employeeLastSessionKey(employeeId) : LEGACY_LAST_SESSION_KEY;
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return normalizeSession(parsed);
  } catch {
    return null;
  }
}

function persistCheckInFlag(employeeId, checkedIn, checkInAt) {
  const store = punchStore();
  if (!store) return;
  if (!employeeId && !allowLegacyMirror()) return;
  const key = employeeId ? employeeCheckInKey(employeeId) : LEGACY_CHECKIN_KEY;
  try {
    if (checkedIn && checkInAt != null) {
      store.setItem(key, String(checkInAt));
      if (employeeId && allowLegacyMirror()) {
        localStorage.setItem(LEGACY_CHECKIN_KEY, String(checkInAt));
      }
    } else {
      store.removeItem(key);
      if (
        allowLegacyMirror() &&
        (!employeeId || employeeId === localStorage.getItem("hrms_current_employee_id"))
      ) {
        localStorage.removeItem(LEGACY_CHECKIN_KEY);
      }
    }
  } catch {
    /* ignore */
  }
}

function persistLastSession(employeeId, session) {
  const store = punchStore();
  if (!store) return;
  if (!employeeId && !allowLegacyMirror()) return;
  const key = employeeId ? employeeLastSessionKey(employeeId) : LEGACY_LAST_SESSION_KEY;
  try {
    store.setItem(key, JSON.stringify(session));
    if (employeeId && allowLegacyMirror()) {
      localStorage.setItem(LEGACY_LAST_SESSION_KEY, JSON.stringify(session));
    }
  } catch {
    /* ignore */
  }
}

/** Sync navbar check-in flag (used by AdminCheckInOut live state). */
export function syncCheckInFlag(employeeId, checkedIn, checkInAt) {
  persistCheckInFlag(employeeId, checkedIn, checkInAt);
}

export function syncLastSession(employeeId, session) {
  persistLastSession(employeeId, session);
}

/**
 * Start a new punch session (append). Does not overwrite First IN.
 * Assigns attendance calendar day using policy inter-shift gap / overnight rules.
 * Returns aggregated day row, or null if already open / invalid.
 */
export function recordAttendanceCheckIn(employeeId, atMs = Date.now()) {
  if (!employeeId) return null;
  const id = String(employeeId);
  const policies = loadAttendancePolicies();
  const policy = resolvePolicyForEmployee({ id }, policies, new Date(atMs)) || {};
  const shift = resolveShiftForEmployeeDay({ id }, policy, null, new Date(atMs));

  let previousClosedOutMs = null;
  try {
    const last = JSON.parse(punchStore()?.getItem(employeeLastSessionKey(id)) || "null");
    previousClosedOutMs = last?.outMs != null ? Number(last.outMs) : null;
  } catch {
    previousClosedOutMs = null;
  }

  const assigned = assignPunchToAttendanceDay({
    punchMs: atMs,
    punchKind: "in",
    previousClosedOutMs,
    shift,
    policy,
  });
  const dayKey = assigned.attendanceDate || getLocalDayKey(atMs);
  const existing = readEmployeeWorklog(id, dayKey);
  const sessions = existing?.sessions ? [...existing.sessions] : [];
  if (sessions.some((s) => s.outMs == null)) {
    return existing;
  }
  sessions.push({ inMs: atMs, outMs: null });
  const aggregated = aggregateFirstInLastOut(sessions);
  writeWorklogDay(id, dayKey, aggregated);
  persistCheckInFlag(id, true, atMs);
  clearLocalCheckoutGuard(id, dayKey);
  return aggregated;
}

/**
 * Close the open session. Day outMs becomes Last OUT (FILO).
 * Overnight OUT attaches to the IN attendance day when policy allows.
 */
export function recordAttendanceCheckOut(employeeId, atMs = Date.now()) {
  if (!employeeId) return null;
  const id = String(employeeId);
  const policies = loadAttendancePolicies();
  const policy = resolvePolicyForEmployee({ id }, policies, new Date(atMs)) || {};
  const shift = resolveShiftForEmployeeDay({ id }, policy, null, new Date(atMs));

  let openSessionInMs = null;
  let openSessionDate = null;
  // Scan yesterday + today for an open session (overnight).
  for (const delta of [0, -1]) {
    const d = new Date(atMs);
    d.setDate(d.getDate() + delta);
    const key = getLocalDayKey(d.getTime());
    const row = readEmployeeWorklog(id, key);
    if (row?.open && row?.inMs) {
      openSessionInMs = row.inMs;
      openSessionDate = key;
      break;
    }
  }

  const assigned = assignPunchToAttendanceDay({
    punchMs: atMs,
    punchKind: "out",
    openSessionInMs,
    openSessionDate,
    shift,
    policy,
  });
  const dayKey = assigned.attendanceDate || openSessionDate || getLocalDayKey(atMs);
  const existing = readEmployeeWorklog(id, dayKey);
  // Collapse orphans before close — at most one open session (A1).
  let sessions = collapseOpenSessions(existing?.sessions || []);
  if (sessions.length === 0 && existing?.inMs) {
    sessions = [{ inMs: existing.inMs, outMs: existing.outMs ?? null }];
    sessions = collapseOpenSessions(sessions);
  }
  if (sessions.length === 0) {
    sessions = [{ inMs: atMs, outMs: atMs }];
  } else {
    let closed = false;
    for (let i = sessions.length - 1; i >= 0; i -= 1) {
      if (sessions[i].outMs == null) {
        sessions[i] = { ...sessions[i], outMs: atMs };
        closed = true;
        break;
      }
    }
    if (!closed) {
      sessions.push({ inMs: atMs, outMs: atMs });
    }
  }
  const aggregated = aggregateFirstInLastOut(sessions);
  writeWorklogDay(id, dayKey, aggregated);
  persistCheckInFlag(id, false, null);
  markLocalCheckoutGuard(id, dayKey);
  const lastClosed = [...sessions].reverse().find((s) => s.outMs != null);
  if (lastClosed) persistLastSession(id, lastClosed);
  return aggregated;
}

export function hasOpenPunchSession(employeeId, dayKey = getLocalDayKey()) {
  const row = readEmployeeWorklog(employeeId, dayKey);
  return Boolean(row?.open);
}

export function clearWorklogDay(employeeId, dayKey) {
  if (!employeeId || !dayKey) return;
  const store = punchStore();
  if (!store) return;
  try {
    const key = employeeWorklogKey(employeeId);
    const raw = store.getItem(key);
    const parsed = raw ? JSON.parse(raw) : {};
    const next = parsed && typeof parsed === "object" ? parsed : {};
    delete next[dayKey];
    store.setItem(key, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  dispatchPunchUpdated(employeeId, dayKey);
}

/**
 * Undo an optimistic check-in when the attendance API rejects or fails.
 */
export function revertOpenCheckIn(employeeId, previousRow, dayKey = getLocalDayKey()) {
  if (!employeeId) return;
  if (!previousRow?.inMs) {
    clearWorklogDay(employeeId, dayKey);
    persistCheckInFlag(employeeId, false, null);
    return;
  }
  writeWorklogDay(employeeId, dayKey, previousRow);
  persistCheckInFlag(
    employeeId,
    Boolean(previousRow.open),
    previousRow.open ? previousRow.inMs : null
  );
}

/**
 * Pair punches into sessions. Consecutive INs keep one open session at the
 * earliest IN (A1) — never emit multiple outMs:null orphans.
 * Lone OUT without an open IN is ignored (does not create a zero-length pair).
 */
function pairPunchesToSessions(punches = []) {
  const sorted = (Array.isArray(punches) ? punches : [])
    .map((p) => ({
      kind: String(p.punchKind || p.punch_kind || "").toLowerCase(),
      ms: msFromServerTime(p.punchedAt || p.punched_at),
    }))
    .filter((p) => p.ms != null)
    .sort((a, b) => a.ms - b.ms);
  const sessions = [];
  let openIn = null;
  for (const p of sorted) {
    if (p.kind === "out") {
      if (openIn != null) {
        sessions.push({ inMs: openIn, outMs: p.ms });
        openIn = null;
      }
      // Lone OUT: ignore for hours (no phantom pair).
    } else {
      // IN while already open: keep earliest open IN (A1).
      if (openIn == null) openIn = p.ms;
    }
  }
  if (openIn != null) sessions.push({ inMs: openIn, outMs: null });
  return sessions;
}

const OPEN_SESSION_MAX_MS = 24 * 60 * 60 * 1000;

/** Closed IN→OUT pairs plus one open IN→now. An open IN past 24h counts as nothing. */
export function workedMsFromPunchRows(punches, nowMs = Date.now()) {
  const sessions = pairPunchesToSessions(punches).filter(
    (s) => s.outMs != null || nowMs - s.inMs < OPEN_SESSION_MAX_MS
  );
  if (!sessions.length) return null;
  const open = sessions.some((s) => s.outMs == null);
  return workedMsFromSessions(
    {
      inMs: sessions[0].inMs,
      outMs: open ? null : sessions[sessions.length - 1].outMs,
      sessions,
      open,
    },
    nowMs
  );
}

function msFromServerTime(v) {
  if (v == null || v === "" || v === "--") return null;
  if (typeof v === "number") return toFiniteMs(v);
  const n = Number(v);
  if (Number.isFinite(n) && n > 1e11) return n;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : toFiniteMs(d.getTime());
}

/**
 * Seed the local worklog from persisted attendance so ESS home / navbar
 * survive a new login, refresh, or closed Access ESS window.
 */
export function hydrateEmployeeWorklogFromAttendance(employeeId, record) {
  if (!employeeId || !record) return null;
  const inMs = msFromServerTime(record.checkIn || record.checkInTime || record.inTime);
  const outRaw = record.checkOut || record.checkOutTime || record.outTime;
  let outMs = outRaw ? msFromServerTime(outRaw) : null;
  // Same-second FILO (check_out copied onto IN) is not a real checkout.
  if (outMs != null && inMs != null && outMs <= inMs) outMs = null;
  let punchSessions = pairPunchesToSessions(record.punches);
  // Prefer punch pairs. Fill FILO Last OUT onto an open IN only when OUT is later.
  if (outMs != null && punchSessions.length > 0) {
    const last = punchSessions[punchSessions.length - 1];
    if (last && last.outMs == null && outMs > last.inMs) {
      punchSessions = punchSessions.map((s, idx) =>
        idx === punchSessions.length - 1 ? { ...s, outMs } : s
      );
    }
  }
  if (inMs == null && punchSessions.length === 0) return null;

  const aggregated =
    punchSessions.length > 0
      ? aggregateFirstInLastOut(punchSessions)
      : aggregateFirstInLastOut([{ inMs, outMs }]);
  if (!aggregated?.inMs) return null;

  const dayKey =
    (record.date && String(record.date).slice(0, 10)) || getLocalDayKey(aggregated.inMs);
  const local = readEmployeeWorklog(employeeId, dayKey);

  // Multiple punches: a new local IN after Last OUT must not be wiped by stale
  // history that still shows First IN + previous Last OUT.
  if (local?.open && !aggregated.open) {
    const openSession = [...(local.sessions || [])]
      .reverse()
      .find((s) => s.outMs == null);
    const localOpenIn = openSession?.inMs ?? local.inMs;
    const serverOut = aggregated.outMs;
    const lastPunch = Array.isArray(record.punches)
      ? record.punches[record.punches.length - 1]
      : null;
    const lastKind = String(
      lastPunch?.punchKind || lastPunch?.punch_kind || ""
    ).toLowerCase();
    const lastPunchOut =
      lastKind === "out"
        ? msFromServerTime(lastPunch.punchedAt || lastPunch.punched_at)
        : null;
    const newerLocalIn =
      localOpenIn != null &&
      ((lastPunchOut != null && localOpenIn > lastPunchOut) ||
        (lastPunchOut == null && (serverOut == null || localOpenIn > serverOut)));
    if (newerLocalIn) {
      persistCheckInFlag(employeeId, true, local.inMs);
      return local;
    }
  }

  // Sticky checkout: never reopen from an IN-only / stale history row after a
  // local close. Allow reopen only when server has a newer IN than local Last OUT
  // (legitimate next session the same day).
  if (aggregated.open) {
    const guarded = hasLocalCheckoutGuard(employeeId, dayKey);
    const localClosed = isClosedWorklogRow(local);
    const lastStored = readLastSessionStored(employeeId);
    const lastOutSameDay =
      lastStored?.outMs != null && getLocalDayKey(lastStored.outMs) === dayKey
        ? Number(lastStored.outMs)
        : null;
    const localOut =
      localClosed && local?.outMs != null ? Number(local.outMs) : lastOutSameDay;
    const serverOpenIn = Number(
      [...(aggregated.sessions || [])].reverse().find((s) => s?.outMs == null)?.inMs ??
        aggregated.inMs
    );

    if (guarded || localClosed || (localOut != null && !local?.open)) {
      const newerInAfterOut =
        localOut != null &&
        Number.isFinite(serverOpenIn) &&
        serverOpenIn > localOut;
      if (!newerInAfterOut) {
        persistCheckInFlag(employeeId, false, null);
        if (localClosed || localOut != null) {
          markLocalCheckoutGuard(employeeId, dayKey);
        }
        return local || null;
      }
    }
  }

  // Avoid rewrite/event storms when hydrate data matches local (Access ESS / refresh).
  if (
    local &&
    Number(local.inMs) === Number(aggregated.inMs) &&
    (local.outMs == null) === (aggregated.outMs == null) &&
    (local.outMs == null || Number(local.outMs) === Number(aggregated.outMs)) &&
    Boolean(local.open) === Boolean(aggregated.open)
  ) {
    return local;
  }

  writeWorklogDay(employeeId, dayKey, aggregated);
  persistCheckInFlag(
    employeeId,
    Boolean(aggregated.open),
    aggregated.open ? aggregated.inMs : null
  );
  if (!aggregated.open) {
    const lastClosed = [...(aggregated.sessions || [])].reverse().find((s) => s.outMs != null);
    if (lastClosed) persistLastSession(employeeId, lastClosed);
    markLocalCheckoutGuard(employeeId, dayKey);
  } else {
    // Self-heal: persist collapsed sessions so dirty multi-open rows stop ticking.
    clearLocalCheckoutGuard(employeeId, dayKey);
  }
  return aggregated;
}

function neighboringAttendanceDayKeys(atMs = Date.now()) {
  const keys = [];
  for (const delta of [-1, 0, 1]) {
    const d = new Date(atMs);
    d.setDate(d.getDate() + delta);
    keys.push(getLocalDayKey(d.getTime()));
  }
  return keys;
}

/** Calendar day key for a my-history row (DATEONLY string or ISO). */
export function attendanceHistoryRowDayKey(record) {
  if (!record?.date) return null;
  const raw = String(record.date);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? getLocalDayKey(ms) : null;
}

function findHistoryRowForDay(rows, dayKey) {
  if (!Array.isArray(rows) || !dayKey) return null;
  const exact = rows.find((r) => attendanceHistoryRowDayKey(r) === dayKey);
  if (exact) return exact;
  return (
    rows.find((r) => {
      const raw = r?.date != null ? String(r.date) : "";
      const ms = Date.parse(raw);
      return Number.isFinite(ms) && getLocalDayKey(ms) === dayKey;
    }) || null
  );
}

function serverRowHasLivePunch(record) {
  if (!record) return false;
  if (record.checkIn || record.checkInTime || record.inTime) return true;
  if (record.checkOut || record.checkOutTime || record.outTime) return true;
  return Array.isArray(record.punches) && record.punches.length > 0;
}

/** History can lag a moment behind the laptop that just punched. */
const LIVE_HISTORY_LAG_MS = 90 * 1000;

/**
 * Seed yesterday/today/tomorrow from my-history so Check In matches the
 * server on another laptop, Access ESS, or a fresh employee login.
 * Drops leftover local OPEN rows with no server punch (stale browser flag).
 * Does not create punches and does not call check-in.
 */
export function hydrateLivePunchWindowFromHistory(
  employeeId,
  rows,
  atMs = Date.now()
) {
  if (!employeeId) return null;
  const id = String(employeeId);
  const dayKeys = neighboringAttendanceDayKeys(atMs);
  let last = null;
  for (const dayKey of dayKeys) {
    const rec = findHistoryRowForDay(rows, dayKey);
    if (rec && serverRowHasLivePunch(rec)) {
      last = hydrateEmployeeWorklogFromAttendance(id, rec) || last;
      continue;
    }
    const local = readEmployeeWorklog(id, dayKey);
    if (!local?.open || !local?.inMs) continue;
    if (Date.now() - Number(local.inMs) < LIVE_HISTORY_LAG_MS) continue;
    revertOpenCheckIn(id, null, dayKey);
  }
  dispatchPunchUpdated(id, getLocalDayKey(atMs));
  return last;
}

/**
 * Find an open FILO session across yesterday / today / tomorrow (overnight + assigner skew).
 * Prefer this over today-only reads when hydrating Check In / Clock Out UI.
 * Respects checkout guard so refresh cannot revive a closed session.
 */
export function findOpenWorklogSession(employeeId, atMs = Date.now()) {
  if (!employeeId) return null;
  const id = String(employeeId);
  const todayKey = getLocalDayKey(atMs);
  const todayRow = readEmployeeWorklog(id, todayKey);
  if (isClosedWorklogRow(todayRow)) {
    return null;
  }
  const last = readLastSessionStored(id);
  if (
    last?.outMs != null &&
    getLocalDayKey(last.outMs) === todayKey &&
    !todayRow?.open
  ) {
    return null;
  }
  for (const delta of [0, -1, 1]) {
    const d = new Date(atMs);
    d.setDate(d.getDate() + delta);
    const key = getLocalDayKey(d.getTime());
    const row = readEmployeeWorklog(id, key);
    if (!row?.open || !row?.inMs) continue;

    if (hasLocalCheckoutGuard(id, key)) {
      // Sticky checkout: ignore stale open rows unless a newer IN after Last OUT.
      const lastOut =
        last?.outMs != null && getLocalDayKey(last.outMs) === key
          ? Number(last.outMs)
          : row.outMs != null
            ? Number(row.outMs)
            : null;
      const openIn = Number(
        [...(row.sessions || [])].reverse().find((s) => s?.outMs == null)?.inMs ??
          row.inMs
      );
      if (lastOut != null && Number.isFinite(openIn) && openIn > lastOut) {
        return { dayKey: key, ...row };
      }
      continue;
    }

    return { dayKey: key, ...row };
  }
  const stored = readStoredCheckIn(id);
  if (stored.checkedIn && stored.at != null) {
    const key = getLocalDayKey(stored.at);
    const row = readEmployeeWorklog(id, key);
    if (isClosedWorklogRow(row) || hasLocalCheckoutGuard(id, key)) {
      return null;
    }
    if (row?.open && row?.inMs) {
      return { dayKey: key, ...row };
    }
  }
  return null;
}
